import { CountryRegionField, StateProvinceField } from '@/components/address-fields';
import { Stack } from '@/components/layout';
import { MutateInfoTemplate } from '@/components/template/mutate-info/mutate-info-template';
import { FormInput } from '@/components/ui';
import Text from '@/components/ui/text/text';
import { STRINGS } from '@/constants/strings';
import { useMutateCoach } from '@/features/coach/hooks/use-mutate-coach';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { MAX_CITY_LENGTH, MAX_PHONE_LENGTH, MAX_ZIP_LENGTH } from '@/validation';
import { Controller, useWatch } from 'react-hook-form';

export function MutateCoachScreen() {
  const logics = useMutateCoach();
  const title = STRINGS.mutateCoach.title;
  const { scale, isTablet } = useScaling();
  const rowGap = scale(16);
  const control = logics.form.control;
  const setValue = logics.form.setValue;
  const country = useWatch({ control, name: 'country' });
  const addressFieldProps = {
    uppercaseLabel: true,
    labelVariant: 'Font Inter/Subtitle/Regular 11px' as const,
    triggerVariant: 'Font Inter/Subtitle/Regular 14px' as const,
    inputVariant: 'Font Inter/Subtitle/Regular 14px' as const,
    labelGap: 8,
    labelStyle: { color: colors['Labels/Primary'] },
    triggerStyle: {
      height: scale(isTablet ? 48 : 44),
      minHeight: scale(isTablet ? 48 : 44),
      borderRadius: scale(4),
      borderColor: colors['5'],
      backgroundColor: colors['Backgrounds/Primary'],
    },
  };

  return (
    <MutateInfoTemplate title={{ create: title, edit: title }} logics={logics}>
      <Stack gap={rowGap} width="100%">
        {isTablet ? (
          <Text variant="Font Inter/Subtitle/Bold 14px" style={{ color: colors.Blue }}>
            {STRINGS.mutateCoach.profile.editContactInfo}
          </Text>
        ) : (
          <Text
            fontFamily="Montserrat"
            fontSize={scale(11)}
            fontWeight="700"
            letterSpacing={0}
            lineHeight={scale(11) * 1.4}
            style={{ color: colors.Blue }}>
            {STRINGS.mutateCoach.profile.editContactInfo}
          </Text>
        )}
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <FormInput
              label={STRINGS.mutateCoach.profile.phoneNumber}
              required={false}
              value={value}
              maxLength={MAX_PHONE_LENGTH}
              trim
              status={error?.message ? 'error' : 'default'}
              errorMessage={error?.message}
              keyboardType="phone-pad"
              onBlur={onBlur}
              onChangeText={altPhoneNumber => onChange(altPhoneNumber)}
              placeholder={''}
              onCountryCodeChange={countryCode => setValue('countryCode', countryCode)}
              phoneNumber
            />
          )}
        />
        <Stack direction={isTablet ? 'row' : 'column'} gap={rowGap} width="100%">
          <Stack flex={isTablet ? 1 : undefined}>
            <Controller
              name="country"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CountryRegionField
                  {...addressFieldProps}
                  label={STRINGS.mutateCoach.profile.country}
                  required={false}
                  value={value}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  onValueChange={nextCountry => {
                    onChange(nextCountry);
                    setValue('state', '', { shouldDirty: true });
                  }}
                />
              )}
            />
          </Stack>
          <Stack flex={isTablet ? 1 : undefined}>
            <Controller
              name="state"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <StateProvinceField
                  {...addressFieldProps}
                  label={STRINGS.mutateCoach.profile.state}
                  required={false}
                  country={country}
                  value={value}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  onValueChange={onChange}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction={isTablet ? 'row' : 'column'} gap={rowGap} width="100%">
          <Stack flex={isTablet ? 1 : undefined}>
            <Controller
              name="city"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <FormInput
                  label={STRINGS.mutateCoach.profile.city}
                  required={false}
                  value={value}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  maxLength={MAX_CITY_LENGTH}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  placeholder={STRINGS.mutateCoach.profile.city}
                />
              )}
            />
          </Stack>
          <Stack flex={isTablet ? 1 : undefined}>
            <Controller
              name="zipCode"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <FormInput
                  label={STRINGS.mutateCoach.profile.zipCode}
                  required={false}
                  value={value}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  keyboardType="numeric"
                  maxLength={MAX_ZIP_LENGTH}
                  onBlur={onBlur}
                  onChangeText={zipCode => onChange(zipCode.replace(/[^0-9]/g, ''))}
                  placeholder={STRINGS.mutateCoach.profile.zipCode}
                />
              )}
            />
          </Stack>
        </Stack>
      </Stack>
    </MutateInfoTemplate>
  );
}
